import React from 'react';
import { Alert } from 'reactstrap';

const titleize = value =>
  (value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, match => match.toUpperCase());

const formatQuote = quote => {
  if (!quote || !quote.quote) {
    return 'Quote pending';
  }

  const range = quote.quote.shipping_fee_range;
  if (range && range.min != null && range.max != null) {
    return `$${parseFloat(range.min).toFixed(2)} - $${parseFloat(range.max).toFixed(2)}`;
  }

  if (quote.quote.shipping_fee != null) {
    return `$${parseFloat(quote.quote.shipping_fee).toFixed(2)}`;
  }

  return 'Quote pending';
};

const formatItem = item => {
  const dimensions = item && item.dimensions
    ? ` • ${item.dimensions.length || '?'} x ${item.dimensions.width || '?'} x ${item.dimensions.height || '?'} in`
    : '';
  const weight = item && item.weight ? ` • ${item.weight} lb` : '';
  return `${item.quantity || 0} x ${item.description || 'Item'}${weight}${dimensions}`;
};

const getModeLabel = brokerAssist => {
  const vehicle = (brokerAssist && brokerAssist.vehicleRecommendation) || '';
  const normalized = vehicle.toLowerCase();
  if (brokerAssist && (brokerAssist.is_over_size || normalized.includes('wheeler') || normalized.includes('trailer') || normalized === 'car_hauler')) {
    return 'LTL / FTL';
  }
  return 'Gophr Direct';
};

const formatScheduleLabel = value =>
  (value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, match => match.toUpperCase());

const pillStyle = {
  display: 'inline-block',
  padding: '6px 10px',
  borderRadius: 999,
  backgroundColor: '#ffffff',
  border: '1px solid #525f7f',
  color: '#1d253b',
  fontSize: 12,
  fontWeight: 600,
  marginRight: 8,
  marginBottom: 8
};

const BrokerAssistPanel = ({ brokerAssist, brokerLoading, brokerError, onAnalyze }) => {
  return (
    <div style={{ marginTop: 20, padding: 18, borderRadius: 12, backgroundColor: '#1d253b', border: '1px solid #344675' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ color: '#f7941d', fontSize: 12, fontWeight: 700, letterSpacing: 0.8 }}>GOPHR BROKER</div>
          <div style={{ color: '#ffffff', fontSize: 18, fontWeight: 700 }}>AI shipment planning review</div>
          <div style={{ color: '#d6d9e5', fontSize: 12, marginTop: 4 }}>Recommendations apply to weight, vehicle, tools, and driver notes.</div>
        </div>
        <button
          type="button"
          className="btn btn-default"
          onClick={onAnalyze}
          disabled={brokerLoading}
          style={{ margin: 0 }}
        >
          {brokerLoading ? 'Analyzing...' : brokerAssist ? 'Refresh Broker Plan' : 'Analyze with Gophr Broker'}
        </button>
      </div>

      {brokerError && <Alert color="warning" style={{ marginTop: 16, marginBottom: 0 }}>{brokerError}</Alert>}

      {brokerAssist && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ ...pillStyle, backgroundColor: '#f7941d', borderColor: '#f7941d', color: '#111' }}>{getModeLabel(brokerAssist)}</span>
            <span style={{ color: '#ffffff', fontSize: 16, fontWeight: 700 }}>
              {titleize(brokerAssist.vehicleRecommendation || 'Recommendation unavailable')}
            </span>
          </div>

          <div style={{ marginTop: 12 }}>
            {brokerAssist.total_weight != null && <span style={pillStyle}>{brokerAssist.total_weight} lb total</span>}
            {brokerAssist.distance != null && <span style={pillStyle}>{parseFloat(brokerAssist.distance).toFixed(1)} mi</span>}
            {brokerAssist.duration != null && <span style={pillStyle}>{parseFloat(brokerAssist.duration).toFixed(1)} hr</span>}
            {brokerAssist.estimated_load_minutes != null && <span style={pillStyle}>{brokerAssist.estimated_load_minutes} min load</span>}
            {brokerAssist.estimated_unload_minutes != null && <span style={pillStyle}>{brokerAssist.estimated_unload_minutes} min unload</span>}
            {brokerAssist.schedule_recommendation && <span style={pillStyle}>{formatScheduleLabel(brokerAssist.schedule_recommendation)}</span>}
            {brokerAssist.is_over_size && <span style={pillStyle}>Oversize handling</span>}
          </div>

          {(brokerAssist.schedule_reasoning || brokerAssist.pickup_hours || brokerAssist.dropoff_hours) && (
            <div style={{ marginTop: 16 }}>
              <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Scheduling guidance</div>
              {brokerAssist.schedule_reasoning && <div style={{ color: '#d6d9e5', marginBottom: 6 }}>{brokerAssist.schedule_reasoning}</div>}
              {brokerAssist.pickup_hours && <div style={{ color: '#d6d9e5', marginBottom: 4 }}>Pickup hours: {brokerAssist.pickup_hours}</div>}
              {brokerAssist.dropoff_hours && <div style={{ color: '#d6d9e5' }}>Dropoff hours: {brokerAssist.dropoff_hours}</div>}
            </div>
          )}

          {(brokerAssist.pickup_site_orientation || brokerAssist.dropoff_site_orientation) && (
            <div style={{ marginTop: 16 }}>
              <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Site orientation</div>
              {brokerAssist.pickup_site_orientation && <div style={{ color: '#d6d9e5', marginBottom: 4 }}>Pickup: {brokerAssist.pickup_site_orientation}</div>}
              {brokerAssist.dropoff_site_orientation && <div style={{ color: '#d6d9e5' }}>Dropoff: {brokerAssist.dropoff_site_orientation}</div>}
            </div>
          )}

          {Array.isArray(brokerAssist.equipmentAndTools) && brokerAssist.equipmentAndTools.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Suggested equipment</div>
              <div>
                {brokerAssist.equipmentAndTools.map(tool => (
                  <span key={tool} style={pillStyle}>{titleize(tool)}</span>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(brokerAssist.quotes) && brokerAssist.quotes.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Broker quote range</div>
              {brokerAssist.quotes.map(quote => (
                <div key={quote.type} style={{ display: 'flex', justifyContent: 'space-between', color: '#ffffff', marginBottom: 6 }}>
                  <span>{titleize(quote.type)}</span>
                  <span style={{ color: '#f7941d', fontWeight: 700 }}>{formatQuote(quote)}</span>
                </div>
              ))}
            </div>
          )}

          {Array.isArray(brokerAssist.items) && brokerAssist.items.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Broker item readback</div>
              {brokerAssist.items.slice(0, 3).map((item, index) => (
                <div key={`${item.description || 'item'}-${index}`} style={{ color: '#d6d9e5', marginBottom: 6 }}>
                  {formatItem(item)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BrokerAssistPanel;